import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  MessageSquare, Search, Plus, Send, Paperclip, X, Trash2,
  FileText, Download, ArrowLeft, Loader2, Maximize2,
} from 'lucide-react';
import { chatService } from '../../services';
import { Modal } from '../../components';
import UserPhoto, { resolveUploadUrl } from '../../components/UserPhoto';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-hot-toast';

/**
 * Messages — private one-to-one staff chat.
 *
 * Scope is one organisation: the contact list, every thread and every message
 * are filtered server-side by the caller's organization_id, and a thread is
 * only ever visible to its two participants.
 *
 * This has nothing to do with the "Ask Patch" AI widget in the top header,
 * which is a different thing entirely.
 *
 * Delivery is polling, not sockets: the conversation list refreshes on a slow
 * interval, and the open thread asks only for messages newer than the last id
 * it holds, so a long conversation never refetches its history.
 */

const CONVERSATION_POLL_MS = 12000;
const MESSAGE_POLL_MS = 5000;

const initials = (name) => {
  if (!name) return '?';
  return name.trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase()).join('') || '?';
};

const formatTime = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

// Conversation-list stamp: time today, "Yesterday", weekday this week, else a date.
const formatListStamp = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return formatTime(iso);
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  if (now - d < 7 * 24 * 60 * 60 * 1000) return d.toLocaleDateString([], { weekday: 'short' });
  return d.toLocaleDateString([], { day: 'numeric', month: 'short' });
};

// Day divider label between message groups.
const formatDayLabel = (iso) => {
  const d = new Date(iso);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return 'Today';
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString([], { day: 'numeric', month: 'long', year: 'numeric' });
};

const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Extensions that mark a file as an image whatever mime the uploader claimed.
const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp', '.heic', '.heif', '.gif'];

/**
 * Whether an attachment should be shown as a picture.
 *
 * The API now decides this and sends `is_image`, so both clients always agree.
 * The mime and extension checks are the fallback for messages stored before
 * that flag existed — a photo uploaded from the phone was saved as
 * `application/octet-stream`, and those older messages must still preview.
 */
const isImage = (file) => {
  if (!file) return false;
  if (file.is_image === true) return true;
  if (typeof file.mime === 'string' && file.mime.startsWith('image/')) return true;
  const name = (file.name || file.path || '').toLowerCase();
  return IMAGE_EXTENSIONS.some((ext) => name.endsWith(ext));
};

/** Circular avatar with initials fallback — used in every list and bubble. */
const Avatar = ({ user, size = 44 }) => (
  <div
    className="rounded-full bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300 font-semibold flex items-center justify-center shrink-0 overflow-hidden"
    style={{ width: size, height: size, fontSize: size / 2.6 }}
  >
    <UserPhoto
      src={user?.avatar_url}
      alt={user?.name || ''}
      className="w-full h-full rounded-full"
      fallback={<span>{initials(user?.name)}</span>}
    />
  </div>
);

/**
 * Full-size image overlay, shown over the page rather than in a new tab.
 *
 * A new tab loses the conversation: the user has to switch back to carry on
 * reading, and on an S3 setup the tab lands on a bare presigned URL with no
 * context at all. This keeps them in the thread — Escape or a click outside
 * returns them to exactly where they were.
 *
 * Rendered through a portal so it escapes the message list's scroll container
 * and overflow clipping, the same reason the shared Modal does it.
 */
const ImageLightbox = ({ file, onClose }) => {
  // Escape closes it, which is what anyone expects of a full-screen overlay.
  //
  // Guarded on `file` because this component stays mounted with file === null
  // while nothing is open — without the guard it would pin body overflow to
  // hidden for the whole session and kill scrolling everywhere.
  useEffect(() => {
    if (!file) return undefined;

    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    // Stop the page behind from scrolling under the overlay.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [file, onClose]);

  if (!file) return null;

  const url = resolveUploadUrl(file.path);

  return createPortal(
    <div
      className="fixed inset-0 z-[120] bg-black/80 backdrop-blur-sm flex flex-col"
      // Clicking the backdrop closes; clicks on the picture itself don't
      // bubble up (see the img's stopPropagation below).
      onClick={onClose}
    >
      <div className="flex items-center justify-between gap-4 px-5 py-3 text-white/90">
        <div className="min-w-0">
          <p className="text-[14px] font-medium truncate">{file.name}</p>
          {file.size > 0 && (
            <p className="text-[12px] text-white/60">{formatBytes(file.size)}</p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            title="Open original in a new tab"
            className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition"
          >
            <Download className="w-[18px] h-[18px]" />
          </a>
          <button
            onClick={onClose}
            title="Close (Esc)"
            className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="flex-1 min-h-0 flex items-center justify-center px-5 pb-6">
        <img
          src={url}
          alt={file.name}
          onClick={(e) => e.stopPropagation()}
          className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
        />
      </div>
    </div>,
    document.body
  );
};

/**
 * One attachment inside a message bubble. Images preview inline and open in the
 * in-page lightbox; anything else is a file card that opens in a new tab —
 * a document has nothing to show inline, and stored paths on S3 resolve through
 * a redirect to a presigned URL, so a plain link is the right thing there.
 */
const AttachmentTile = ({ file, mine, onOpenImage }) => {
  const url = resolveUploadUrl(file.path);
  // An image whose file is missing or unreadable falls back to the file card,
  // so a broken-image icon never ends up sitting in a conversation.
  const [imageFailed, setImageFailed] = useState(false);

  if (isImage(file) && !imageFailed) {
    return (
      <button
        type="button"
        onClick={() => onOpenImage(file)}
        title="View full size"
        className="block mt-1.5 first:mt-0 group/img relative cursor-pointer"
      >
        <img
          src={url}
          alt={file.name}
          onError={() => setImageFailed(true)}
          className="max-w-[240px] max-h-[280px] rounded-xl object-cover border border-black/5"
        />
        {/* Hover affordance — the picture is already visible, so this only has
            to signal that clicking gets you the full-size version. */}
        <span className="absolute inset-0 rounded-xl bg-black/0 group-hover/img:bg-black/25 transition-colors flex items-center justify-center">
          <Maximize2 className="w-5 h-5 text-white opacity-0 group-hover/img:opacity-100 transition-opacity drop-shadow" />
        </span>
      </button>
    );
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className={`mt-1.5 first:mt-0 flex items-center gap-2.5 px-3 py-2.5 rounded-xl border transition ${
        mine
          ? 'bg-white/15 border-white/25 hover:bg-white/25'
          : 'bg-gray-50 dark:bg-[#262b33] border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-[#31373f]'
      }`}
    >
      <FileText className={`w-5 h-5 shrink-0 ${mine ? 'text-white' : 'text-blue-600 dark:text-blue-400'}`} />
      <div className="min-w-0">
        <p className="text-[13px] font-medium truncate max-w-[170px]">{file.name}</p>
        <p className={`text-[11px] ${mine ? 'text-white/70' : 'text-gray-400'}`}>{formatBytes(file.size)}</p>
      </div>
      <Download className={`w-4 h-4 shrink-0 ml-1 ${mine ? 'text-white/80' : 'text-gray-400'}`} />
    </a>
  );
};

/** New-conversation picker: every active colleague in my organisation. */
const NewChatModal = ({ open, onClose, onPick }) => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!open) return;
    setSearch('');
    setLoading(true);
    chatService
      .getContacts()
      .then((res) => setContacts(res.data || []))
      .catch((err) => toast.error(err.message || 'Could not load contacts'))
      .finally(() => setLoading(false));
  }, [open]);

  // Filtering is local: an organisation's staff list is small enough that a
  // request per keystroke would cost more than it saves.
  const term = search.trim().toLowerCase();
  const visible = term
    ? contacts.filter(
        (c) =>
          (c.name || '').toLowerCase().includes(term) ||
          (c.email || '').toLowerCase().includes(term)
      )
    : contacts;

  return (
    <Modal isOpen={open} onClose={onClose} title="New message">
      <div className="relative mb-4">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          autoFocus
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search your organisation..."
          className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-[#E2E8F0] dark:border-gray-700 bg-white dark:bg-[#31373f] text-[14px] outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-500/10"
        />
      </div>

      <div className="max-h-[380px] overflow-y-auto -mx-1 px-1">
        {loading && (
          <div className="py-10 flex justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
          </div>
        )}

        {!loading && visible.length === 0 && (
          <p className="py-10 text-center text-[13px] text-gray-400">
            {contacts.length === 0
              ? 'No one else in your organisation yet.'
              : 'No one matches that search.'}
          </p>
        )}

        {!loading &&
          visible.map((c) => (
            <button
              key={c.id}
              onClick={() => onPick(c)}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-gray-50 dark:hover:bg-[#31373f] transition text-left cursor-pointer"
            >
              <Avatar user={c} size={40} />
              <div className="min-w-0 flex-1">
                <p className="text-[14px] font-semibold text-gray-800 dark:text-gray-100 truncate">{c.name}</p>
                <p className="text-[12px] text-gray-400 truncate">{c.role_name || c.email}</p>
              </div>
            </button>
          ))}
      </div>
    </Modal>
  );
};

const Messages = () => {
  const { user } = useAuth();
  const myId = user?.id;

  const [conversations, setConversations] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [activeUser, setActiveUser] = useState(null);

  const [messages, setMessages] = useState([]);
  const [loadingThread, setLoadingThread] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);

  const [draft, setDraft] = useState('');
  const [pendingFiles, setPendingFiles] = useState([]);
  const [sending, setSending] = useState(false);

  const [search, setSearch] = useState('');
  const [newChatOpen, setNewChatOpen] = useState(false);
  // The attachment currently open full size, or null. Held on the page rather
  // than inside each tile so only one overlay can ever be mounted.
  const [lightboxFile, setLightboxFile] = useState(null);

  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  // The poller reads these instead of closing over stale state.
  const activeIdRef = useRef(null);
  const newestIdRef = useRef(null);

  useEffect(() => { activeIdRef.current = activeId; }, [activeId]);

  const scrollToBottom = useCallback((behavior = 'auto') => {
    // Wait for the new bubble to be laid out before measuring scrollHeight.
    requestAnimationFrame(() => {
      const el = scrollRef.current;
      if (el) el.scrollTo({ top: el.scrollHeight, behavior });
    });
  }, []);

  const loadConversations = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoadingList(true);
      const res = await chatService.getConversations();
      setConversations(res.data || []);
    } catch (err) {
      if (!silent) toast.error(err.message || 'Could not load conversations');
    } finally {
      if (!silent) setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    loadConversations();
    const timer = setInterval(() => loadConversations({ silent: true }), CONVERSATION_POLL_MS);
    return () => clearInterval(timer);
  }, [loadConversations]);

  // Opening a thread: load the newest page, clear its unread badge locally so
  // the list reacts immediately, and tell the server we've read it.
  const openConversation = useCallback(async (conversation) => {
    setActiveId(conversation.id);
    setActiveUser(conversation.other_user);
    setMessages([]);
    setDraft('');
    setPendingFiles([]);
    newestIdRef.current = null;
    setLoadingThread(true);

    try {
      const res = await chatService.getMessages(conversation.id);
      const list = res.data || [];
      setMessages(list);
      setHasMore(!!res.has_more);
      if (res.other_user) setActiveUser(res.other_user);
      newestIdRef.current = list.length ? list[list.length - 1].id : null;
      scrollToBottom();

      setConversations((prev) =>
        prev.map((c) => (c.id === conversation.id ? { ...c, unread_count: 0 } : c))
      );
      await chatService.markRead(conversation.id);
    } catch (err) {
      toast.error(err.message || 'Could not open conversation');
    } finally {
      setLoadingThread(false);
    }
  }, [scrollToBottom]);

  // Incremental poll for the open thread — asks only for ids above the newest
  // message already on screen.
  useEffect(() => {
    if (!activeId) return undefined;

    const tick = async () => {
      const conversationId = activeIdRef.current;
      if (!conversationId || document.hidden) return;
      try {
        const res = await chatService.getMessages(conversationId, {
          after: newestIdRef.current || undefined,
        });
        const fresh = res.data || [];
        if (fresh.length === 0) return;
        // The conversation may have been switched while the request was in
        // flight — dropping the response is safer than mixing threads.
        if (activeIdRef.current !== conversationId) return;

        setMessages((prev) => {
          const seen = new Set(prev.map((m) => m.id));
          const added = fresh.filter((m) => !seen.has(m.id));
          return added.length ? [...prev, ...added] : prev;
        });
        newestIdRef.current = fresh[fresh.length - 1].id;
        scrollToBottom('smooth');
        setConversations((prev) =>
          prev.map((c) => (c.id === conversationId ? { ...c, unread_count: 0 } : c))
        );
        chatService.markRead(conversationId).catch(() => {});
      } catch {
        // A failed poll is not worth a toast — the next tick retries.
      }
    };

    const timer = setInterval(tick, MESSAGE_POLL_MS);
    return () => clearInterval(timer);
  }, [activeId, scrollToBottom]);

  const loadOlder = async () => {
    if (!activeId || messages.length === 0 || loadingOlder) return;
    setLoadingOlder(true);
    const el = scrollRef.current;
    const previousHeight = el?.scrollHeight || 0;
    try {
      const res = await chatService.getMessages(activeId, { before: messages[0].id });
      const older = res.data || [];
      if (older.length) {
        setMessages((prev) => [...older, ...prev]);
        setHasMore(!!res.has_more);
        // Hold the reading position: after prepending, scroll down by exactly
        // how much taller the list got, so the view doesn't jump.
        requestAnimationFrame(() => {
          if (el) el.scrollTop = el.scrollHeight - previousHeight;
        });
      } else {
        setHasMore(false);
      }
    } catch (err) {
      toast.error(err.message || 'Could not load older messages');
    } finally {
      setLoadingOlder(false);
    }
  };

  const handlePickContact = async (contact) => {
    setNewChatOpen(false);
    try {
      const res = await chatService.openConversation(contact.id);
      const conversation = res.data;
      // Find-or-create: only add a row if this thread isn't already listed.
      setConversations((prev) =>
        prev.some((c) => c.id === conversation.id) ? prev : [conversation, ...prev]
      );
      openConversation(conversation);
    } catch (err) {
      toast.error(err.message || 'Could not start conversation');
    }
  };

  const handleFiles = (event) => {
    const picked = Array.from(event.target.files || []);
    if (picked.length === 0) return;
    setPendingFiles((prev) => {
      const combined = [...prev, ...picked];
      if (combined.length > 5) {
        toast.error('Up to 5 files per message.');
        return combined.slice(0, 5);
      }
      return combined;
    });
    // Reset so picking the same file twice in a row still fires onChange.
    event.target.value = '';
  };

  const handleSend = async () => {
    const body = draft.trim();
    if (!activeId || sending || (!body && pendingFiles.length === 0)) return;

    setSending(true);
    try {
      const res = await chatService.sendMessage(activeId, { body, files: pendingFiles });
      const message = res.data;
      setMessages((prev) => [...prev, message]);
      newestIdRef.current = message.id;
      setDraft('');
      setPendingFiles([]);
      if (textareaRef.current) textareaRef.current.style.height = 'auto';
      scrollToBottom('smooth');

      // Update the list preview without waiting for the next poll.
      setConversations((prev) =>
        prev
          .map((c) =>
            c.id === activeId
              ? {
                  ...c,
                  last_message_at: message.created_at,
                  last_message_preview:
                    body || (message.attachments?.length === 1
                      ? message.attachments[0].name
                      : `${message.attachments?.length || 0} attachments`),
                  last_message_sender_id: myId,
                  unread_count: 0,
                }
              : c
          )
          .sort((a, b) => new Date(b.last_message_at || 0) - new Date(a.last_message_at || 0))
      );
    } catch (err) {
      toast.error(err.message || 'Message not sent');
    } finally {
      setSending(false);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    try {
      await chatService.deleteMessage(messageId);
      setMessages((prev) =>
        prev.map((m) =>
          m.id === messageId ? { ...m, is_deleted: true, body: null, attachments: [] } : m
        )
      );
    } catch (err) {
      toast.error(err.message || 'Could not delete message');
    }
  };

  const term = search.trim().toLowerCase();
  const visibleConversations = term
    ? conversations.filter((c) => (c.other_user?.name || '').toLowerCase().includes(term))
    : conversations;

  // Insert a day divider whenever the calendar date changes down the thread.
  const rendered = [];
  let lastDay = null;
  messages.forEach((m) => {
    const day = new Date(m.created_at).toDateString();
    if (day !== lastDay) {
      rendered.push({ kind: 'divider', id: `d-${day}`, label: formatDayLabel(m.created_at) });
      lastDay = day;
    }
    rendered.push({ kind: 'message', ...m });
  });

  return (
    <div className="h-full flex gap-5">
      {/* ---------- Left: conversation list ---------- */}
      <aside
        className={`w-full md:w-[340px] shrink-0 bg-white dark:bg-[#262b33] rounded-2xl border border-gray-100 dark:border-gray-800 flex flex-col overflow-hidden ${
          activeId ? 'hidden md:flex' : 'flex'
        }`}
      >
        <div className="px-5 pt-5 pb-4 border-b border-gray-100 dark:border-gray-800">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-[19px] font-bold text-gray-800 dark:text-gray-100">Messages</h1>
            <button
              onClick={() => setNewChatOpen(true)}
              title="New message"
              className="w-9 h-9 rounded-full bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center transition cursor-pointer"
            >
              <Plus className="w-5 h-5" />
            </button>
          </div>

          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search chats"
              className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-[#E2E8F0] dark:border-gray-700 bg-white dark:bg-[#31373f] text-[14px] outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-500/10"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto py-2">
          {loadingList && (
            <div className="py-12 flex justify-center">
              <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
            </div>
          )}

          {!loadingList && visibleConversations.length === 0 && (
            <div className="px-6 py-14 text-center">
              <MessageSquare className="w-10 h-10 mx-auto text-gray-300 dark:text-gray-600 mb-3" strokeWidth={1.5} />
              <p className="text-[14px] font-semibold text-gray-600 dark:text-gray-300">No conversations yet</p>
              <p className="text-[12.5px] text-gray-400 mt-1">
                Start a chat with anyone in your organisation.
              </p>
            </div>
          )}

          {!loadingList &&
            visibleConversations.map((c) => {
              const active = c.id === activeId;
              const unread = c.unread_count > 0;
              return (
                <button
                  key={c.id}
                  onClick={() => openConversation(c)}
                  className={`w-full flex items-center gap-3 px-4 py-3 mx-0 transition text-left cursor-pointer ${
                    active
                      ? 'bg-blue-50 dark:bg-[#31373f]'
                      : 'hover:bg-gray-50 dark:hover:bg-[#31373f]'
                  }`}
                >
                  <Avatar user={c.other_user} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <p
                        className={`text-[14.5px] truncate ${
                          unread
                            ? 'font-bold text-gray-900 dark:text-white'
                            : 'font-semibold text-gray-800 dark:text-gray-100'
                        }`}
                      >
                        {c.other_user?.name}
                      </p>
                      <span className="text-[11px] text-gray-400 shrink-0">
                        {formatListStamp(c.last_message_at)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-2 mt-0.5">
                      <p
                        className={`text-[12.5px] truncate ${
                          unread ? 'text-gray-700 dark:text-gray-200 font-medium' : 'text-gray-400'
                        }`}
                      >
                        {c.last_message_sender_id === myId && c.last_message_preview ? 'You: ' : ''}
                        {c.last_message_preview || c.other_user?.role_name || 'No messages yet'}
                      </p>
                      {unread && (
                        <span className="shrink-0 min-w-[20px] h-5 px-1.5 rounded-full bg-blue-600 text-white text-[11px] font-bold flex items-center justify-center">
                          {c.unread_count > 99 ? '99+' : c.unread_count}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
        </div>
      </aside>

      {/* ---------- Right: thread ---------- */}
      <section
        className={`flex-1 bg-white dark:bg-[#262b33] rounded-2xl border border-gray-100 dark:border-gray-800 flex-col overflow-hidden ${
          activeId ? 'flex' : 'hidden md:flex'
        }`}
      >
        {!activeId && (
          <div className="flex-1 flex flex-col items-center justify-center px-8 text-center">
            <div className="w-16 h-16 rounded-2xl bg-blue-50 dark:bg-blue-500/10 flex items-center justify-center mb-4">
              <MessageSquare className="w-8 h-8 text-blue-500" strokeWidth={1.5} />
            </div>
            <p className="text-[16px] font-bold text-gray-700 dark:text-gray-200">Your messages</p>
            <p className="text-[13px] text-gray-400 mt-1.5 max-w-[300px]">
              Pick a conversation on the left, or start a new one with someone in your organisation.
            </p>
          </div>
        )}

        {activeId && (
          <>
            {/* Thread header */}
            <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-800 flex items-center gap-3">
              <button
                onClick={() => setActiveId(null)}
                className="md:hidden w-9 h-9 rounded-full hover:bg-gray-100 dark:hover:bg-[#31373f] flex items-center justify-center cursor-pointer"
              >
                <ArrowLeft className="w-5 h-5 text-gray-500" />
              </button>
              <Avatar user={activeUser} size={42} />
              <div className="min-w-0">
                <p className="text-[15px] font-bold text-gray-800 dark:text-gray-100 truncate">
                  {activeUser?.name}
                </p>
                <p className="text-[12px] text-gray-400 truncate">
                  {activeUser?.role_name || activeUser?.email}
                </p>
              </div>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-5 bg-[#F8FAFC] dark:bg-[#1f242b]">
              {loadingThread && (
                <div className="py-12 flex justify-center">
                  <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
                </div>
              )}

              {!loadingThread && hasMore && (
                <div className="flex justify-center mb-4">
                  <button
                    onClick={loadOlder}
                    disabled={loadingOlder}
                    className="px-4 py-1.5 rounded-full bg-white dark:bg-[#31373f] border border-gray-200 dark:border-gray-700 text-[12.5px] font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 transition cursor-pointer disabled:opacity-60"
                  >
                    {loadingOlder ? 'Loading...' : 'Load earlier messages'}
                  </button>
                </div>
              )}

              {!loadingThread && messages.length === 0 && (
                <div className="py-16 text-center">
                  <p className="text-[13.5px] text-gray-400">
                    No messages yet — say hello to {activeUser?.name?.split(' ')[0]}.
                  </p>
                </div>
              )}

              {rendered.map((item) => {
                if (item.kind === 'divider') {
                  return (
                    <div key={item.id} className="flex items-center justify-center my-4">
                      <span className="px-3 py-1 rounded-full bg-white dark:bg-[#31373f] border border-gray-100 dark:border-gray-700 text-[11.5px] font-medium text-gray-400">
                        {item.label}
                      </span>
                    </div>
                  );
                }

                const mine = item.sender_id === myId;
                return (
                  <div
                    key={item.id}
                    className={`group flex items-end gap-2 mb-3 ${mine ? 'justify-end' : 'justify-start'}`}
                  >
                    {!mine && <Avatar user={item.sender} size={30} />}

                    {/* Delete sits outside the bubble and only for my own
                        messages — the sender is the only person allowed to
                        remove one. */}
                    {mine && !item.is_deleted && (
                      <button
                        onClick={() => handleDeleteMessage(item.id)}
                        title="Delete message"
                        className="opacity-0 group-hover:opacity-100 transition w-7 h-7 rounded-full hover:bg-red-50 dark:hover:bg-red-500/10 flex items-center justify-center cursor-pointer"
                      >
                        <Trash2 className="w-3.5 h-3.5 text-gray-400 hover:text-red-500" />
                      </button>
                    )}

                    <div
                      className={`max-w-[72%] px-3.5 py-2.5 rounded-2xl ${
                        item.is_deleted
                          ? 'bg-gray-100 dark:bg-[#31373f] text-gray-400 italic rounded-bl-md'
                          : mine
                          ? 'bg-blue-600 text-white rounded-br-md'
                          : 'bg-white dark:bg-[#31373f] text-gray-800 dark:text-gray-100 border border-gray-100 dark:border-gray-700 rounded-bl-md'
                      }`}
                    >
                      {item.is_deleted ? (
                        <p className="text-[13.5px]">This message was deleted</p>
                      ) : (
                        <>
                          {item.attachments?.map((file, i) => (
                            <AttachmentTile
                              key={i}
                              file={file}
                              mine={mine}
                              onOpenImage={setLightboxFile}
                            />
                          ))}
                          {item.body && (
                            <p className="text-[14px] leading-relaxed whitespace-pre-wrap break-words mt-1.5 first:mt-0">
                              {item.body}
                            </p>
                          )}
                        </>
                      )}
                      <p className={`text-[10.5px] mt-1 ${mine && !item.is_deleted ? 'text-white/70' : 'text-gray-400'}`}>
                        {formatTime(item.created_at)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Composer. This used to reserve 150px on the right for the
                "Ask Patch" launcher, which was pinned to the bottom-right
                corner of every page; it now lives in the top header, so the
                Send button can have the full width back. */}
            <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-800">
              {pendingFiles.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2.5">
                  {pendingFiles.map((file, i) => (
                    <div
                      key={`${file.name}-${i}`}
                      className="flex items-center gap-2 pl-2.5 pr-1.5 py-1.5 rounded-lg bg-gray-100 dark:bg-[#31373f] border border-gray-200 dark:border-gray-700"
                    >
                      <FileText className="w-3.5 h-3.5 text-blue-500 shrink-0" />
                      <span className="text-[12px] text-gray-600 dark:text-gray-300 max-w-[150px] truncate">
                        {file.name}
                      </span>
                      <button
                        onClick={() => setPendingFiles((prev) => prev.filter((_, idx) => idx !== i))}
                        className="w-5 h-5 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 flex items-center justify-center cursor-pointer"
                      >
                        <X className="w-3 h-3 text-gray-500" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex items-end gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  hidden
                  onChange={handleFiles}
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  title="Attach files"
                  className="w-10 h-10 shrink-0 rounded-full hover:bg-gray-100 dark:hover:bg-[#31373f] flex items-center justify-center transition cursor-pointer"
                >
                  <Paperclip className="w-[18px] h-[18px] text-gray-400" />
                </button>

                <textarea
                  ref={textareaRef}
                  rows={1}
                  value={draft}
                  onChange={(e) => {
                    setDraft(e.target.value);
                    // Grow with the text, up to a cap, then scroll internally.
                    e.target.style.height = 'auto';
                    e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                  placeholder="Write a message..."
                  className="flex-1 resize-none px-4 py-2.5 rounded-2xl border border-[#E2E8F0] dark:border-gray-700 bg-white dark:bg-[#31373f] text-[14px] leading-relaxed outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-500/10 max-h-[120px]"
                />

                <button
                  onClick={handleSend}
                  disabled={sending || (!draft.trim() && pendingFiles.length === 0)}
                  className="w-10 h-10 shrink-0 rounded-full bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white flex items-center justify-center transition cursor-pointer"
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-[18px] h-[18px]" />}
                </button>
              </div>
            </div>
          </>
        )}
      </section>

      <NewChatModal
        open={newChatOpen}
        onClose={() => setNewChatOpen(false)}
        onPick={handlePickContact}
      />

      <ImageLightbox file={lightboxFile} onClose={() => setLightboxFile(null)} />
    </div>
  );
};

export default Messages;
